import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Modal, Select, Input, InputNumber, DatePicker, Tooltip, Empty, Badge, message } from "antd";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import moment from "moment";
import { FiAlertTriangle, FiClock, FiRotateCcw } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { FaRocket, FaPhoneAlt, FaCheckCircle, FaFileInvoiceDollar, FaTrophy, FaTimesCircle } from "react-icons/fa";
import DataParser from "../../../utlits/Dataparser";
import CrmLeadsServices from "../../../Services/CrmLeadsServices";
import LeadPipelineService from "../../../Services/LeadPipelineService";
import SocketService from "../../../Services/SocketService";
import CreateCrmLead from "./CreateCrmLead";

const { TextArea } = Input;
const { Option } = Select;

// ── Stage config ─────────────────────────────────────────────────────────────
const STAGES = [
  { key: "new",       label: "New",       color: "#00BFA5", bg: "#E0F7F4", border: "#80DFD2", icon: <img src="/assets/images/new.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  { key: "contacted", label: "Contacted", color: "#3b82f6", bg: "#dbeafe", border: "#93c5fd", icon: <img src="/assets/images/contacted.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  { key: "qualified", label: "Qualified", color: "#f59e0b", bg: "#fef3c7", border: "#fcd34d", icon: <img src="/assets/images/qualified.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  { key: "proposal",  label: "Proposal",  color: "#8b5cf6", bg: "#f3e8ff", border: "#c4b5fd", icon: <img src="/assets/images/proposal.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  { key: "won",       label: "Won",       color: "#10b981", bg: "#d1fae5", border: "#6ee7b7", icon: <img src="/assets/images/won.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
  { key: "lost",      label: "Lost",      color: "#ef4444", bg: "#fee2e2", border: "#fca5a5", icon: <img src="/assets/images/lost.svg" alt="" style={{ width: "14px", height: "14px" }} /> },
];

// Terminal stages — no moves allowed out; Lost uses /reopen endpoint
const TERMINAL_STAGES = ["won", "lost"];

// Client-side mirror of backend TRANSITION_RULES
// Only lists blocked transitions that need a human-readable reason
const BLOCKED_TRANSITION_REASONS = {
  new: {
    qualified: "Must pass through Contacted before Qualified.",
    proposal:  "Must pass through Contacted and Qualified before Proposal.",
    won:       "Cannot move directly from New to Won.",
  },
  contacted: {
    new: "Backward movement is not allowed.",
    won: "Must pass through Qualified or Proposal before Won.",
  },
  qualified: {
    new:       "Backward movement is not allowed.",
    contacted: "Backward movement is not allowed.",
  },
  proposal: {
    new:       "Backward movement is not allowed.",
    contacted: "Backward movement is not allowed.",
    qualified: "Backward movement is not allowed.",
  },
};

function getTransitionError(fromStage, toStage) {
  return BLOCKED_TRANSITION_REASONS[fromStage]?.[toStage] || null;
}

const LOST_REASONS = [
  { value: "price",       label: "Price" },
  { value: "competitor",  label: "Competitor" },
  { value: "timing",      label: "Timing" },
  { value: "no_response", label: "No Response" },
  { value: "other",       label: "Other" },
];

// ── SLA helper (client-side) ─────────────────────────────────────────────────
function computeSla(lead) {
  if (lead.workflow_type !== "inbound" || lead.stage !== "new") return null;
  const elapsed = (Date.now() - new Date(lead.createdAt || lead.created_at).getTime()) / 3600000;
  if (elapsed < 2) return { status: "green", label: "On Track", color: "#10b981" };
  if (elapsed < 24) return { status: "amber", label: "SLA Approaching", color: "#f59e0b" };
  return { status: "red", label: "SLA Breached", color: "#ef4444" };
}

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 1) return `${Math.floor(diff / 60000)}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Lead card ────────────────────────────────────────────────────────────────
const LeadCard = ({ lead, stageCfg, onDragStart, onDragEnd, onReopen, onOpenLead, snoozed, isBeingDragged }) => {
  const sla = React.useMemo(() => computeSla(lead), [lead]);
  const isDraggable = !TERMINAL_STAGES.includes(lead.stage);
  const leftColor = sla ? sla.color : stageCfg.color;
  const amount = lead.estimated_value || lead.prospect_amount;
  const dragRef = React.useRef(false);

  // Show dashed placeholder in place of the card while it's being dragged
  if (isBeingDragged) {
    return (
      <div style={{
        border: "1.5px dashed #B5B5B5",
        borderRadius: 8,
        marginBottom: 6,
        height: 80,
        background: "transparent",
      }} />
    );
  }

  return (
    <div
      draggable={isDraggable}
      onDragStart={isDraggable ? (e) => {
        if (!dragRef.current) { e.preventDefault(); return; }
        // Force full-opacity ghost — browser default applies transparency
        const el = e.currentTarget;
        const clone = el.cloneNode(true);
        clone.style.cssText += `position:fixed;top:-9999px;left:-9999px;width:${el.offsetWidth}px;opacity:1;`;
        document.body.appendChild(clone);
        const rect = el.getBoundingClientRect();
        e.dataTransfer.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top);
        setTimeout(() => document.body.removeChild(clone), 0);
        onDragStart(e, lead);
      } : undefined}
      onDragEnd={() => { dragRef.current = false; onDragEnd && onDragEnd(); }}
      style={{
        display: "flex",
        background: "#fff",
        borderRadius: 8,
        marginBottom: 6,
        boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
        cursor: "default",
        opacity: snoozed ? 0.55 : 1,
        userSelect: "none",
        overflow: "hidden",
        outline: "none",
        paddingLeft: 8,
      }}
    >
      {/* Left color strip — inset with margin so it doesn't touch top/bottom */}
      <div style={{ width: 3, background: leftColor, flexShrink: 0, margin: "8px 0", borderRadius: 2 }} />
      <div style={{ flex: 1, minWidth: 0, background: "#fff", padding: "10px 12px" }}>

        {/* Top row: SLA badge left, PKR value right */}
        {(sla || (stageCfg.key === "won" ? lead.won_amount : (lead.estimated_value || lead.prospect_amount))) && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, gap: 6 }}>
            {sla ? (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600,
                background: sla.status === "green" ? "#d1fae5" : sla.status === "amber" ? "#fef3c7" : "#fee2e2",
                color: sla.color, flexShrink: 0,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: sla.color, display: "inline-block" }} />
                {sla.label}
              </div>
            ) : <span />}
            {(stageCfg.key === "won" ? lead.won_amount : (lead.estimated_value || lead.prospect_amount)) ? (
              <Tooltip title={`PKR ${Number(stageCfg.key === "won" ? lead.won_amount : (lead.estimated_value || lead.prospect_amount)).toLocaleString()}`}>
                <span style={{ 
                  fontSize: 11, 
                  color: "#10b981", 
                  fontWeight: 600, 
                  whiteSpace: "nowrap", 
                  overflow: "hidden", 
                  textOverflow: "ellipsis",
                  maxWidth: "90px",
                  display: "inline-block",
                  verticalAlign: "middle"
                }}>
                  PKR {Number(stageCfg.key === "won" ? lead.won_amount : (lead.estimated_value || lead.prospect_amount)).toLocaleString()}
                </span>
              </Tooltip>
            ) : null}
          </div>
        )}

        {/* Title — click opens lead detail modal */}
        <div
          onClick={(e) => { e.stopPropagation(); onOpenLead && onOpenLead(lead.id); }}
          style={{
            fontWeight: 500, fontSize: 13, color: "#1e293b", lineHeight: 1.4,
            wordBreak: "break-word", whiteSpace: "normal",
            cursor: "pointer",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "#0DDFAB"}
          onMouseLeave={e => e.currentTarget.style.color = "#1e293b"}
        >
          {lead.lead_title || lead.title || `Lead #${lead.id}`}
        </div>

        {/* Contact */}
        {(lead.lead_contact_name || lead.lead_phone) && (
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
            {lead.lead_contact_name}{lead.lead_contact_name && lead.lead_phone && " · "}{lead.lead_phone}
          </div>
        )}

        {/* Bottom row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
          <span style={{ fontSize: 9.5, color: "#94a3b8", display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
            <FiClock size={10} />
            {timeAgo(lead.last_activity_at)}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            {lead.workflow_type && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                fontSize: 10, fontWeight: 600,
                padding: "2px 7px", borderRadius: 4,
                background: lead.workflow_type === "inbound" ? "#F0F8FF" : "#F0FFF8",
                color: lead.workflow_type === "inbound" ? "#7394B1" : "#4B936F",
              }}>
                <i className={lead.workflow_type === "inbound" ? "uil uil-arrow-down-left" : "uil uil-arrow-up-right"} style={{ fontSize: 11 }} />
                {lead.workflow_type.charAt(0).toUpperCase() + lead.workflow_type.slice(1)}
              </span>
            )}
            {isDraggable && (
              <span
                onMouseDown={() => { dragRef.current = true; }}
                onMouseUp={() => { dragRef.current = false; }}
                style={{ display: "inline-flex", alignItems: "center", cursor: "grab", padding: "2px" }}
              >
                <MdDragIndicator size={16} color="#b0b0b0" />
              </span>
            )}
            {lead.stage === "lost" && (
              <Tooltip title="Re-open lead">
                <button onClick={() => onReopen(lead)} style={{ border: "none", background: "none", cursor: "pointer", color: "#3b82f6", padding: 2, display: "flex" }}>
                  <FiRotateCcw size={12} />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Pipeline column ──────────────────────────────────────────────────────────
const PipelineColumn = ({ stage, leads, onDragStart, onDragOver, onDrop, onReopen, onOpenLead, draggedLeadId, onDragEnd }) => {
  const isTerminal = TERMINAL_STAGES.includes(stage.key);
  const [colHovered, setColHovered] = React.useState(false);

  const stageAmt = React.useMemo(() => {
    const resolveAmt = (l) => Number(l.estimated_value || l.prospect_amount || 0);
    return leads.reduce((s, l) => {
      const val = stage.key === "won" ? (Number(l.won_amount) || resolveAmt(l)) : resolveAmt(l);
      return s + val;
    }, 0);
  }, [leads, stage.key]);
  const amtLabel = stage.key === "won" ? "Total Won" : "Prospect Value";

  return (
    <div
      className="pb-column"
      style={{
        background: "#F2F2F2", borderRadius: 12, padding: "14px 12px",
        border: colHovered ? "1.5px solid #1e293b" : "1.5px solid transparent",
        transition: "border 0.15s ease",
      }}
      onMouseEnter={() => setColHovered(true)}
      onMouseLeave={() => setColHovered(false)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(e, stage.key); }}
      onDrop={(e) => onDrop(e, stage.key)}
    >
      {/* Column header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Stage icon — white rounded square */}
          <div style={{
            width: 30, height: 30, borderRadius: 8, background: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.10)", flexShrink: 0,
          }}>
            {stage.icon}
          </div>
          <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{stage.label}</span>
        </div>
        {/* Count badge — black circle, white text */}
        <span style={{
          background: "#000", color: "#fff",
          borderRadius: "50%", 
          width: "22px",
          height: "22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11, fontWeight: 700,
          flexShrink: 0,
        }}>
          {leads.length}
        </span>
      </div>

      {/* Stage amount total */}
      <Tooltip title={`${amtLabel}: PKR ${stageAmt > 0 ? stageAmt.toLocaleString() : "0"}`}>
        <div style={{
          fontSize: 11, marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid #e2e8f0",
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
          cursor: "pointer",
        }}>
          <span style={{ color: "#94a3b8", fontWeight: 500 }}>{amtLabel}: </span>
          <span style={{ color: "#0DDFAB", fontWeight: 700 }}>PKR {stageAmt > 0 ? stageAmt.toLocaleString() : "0"}</span>
        </div>
      </Tooltip>

      {/* Drop zone indicator */}
      {!isTerminal && leads.length === 0 && (
        <div style={{
          border: `2px dashed ${stage.border}`, borderRadius: 10,
          padding: 24, textAlign: "center", color: "#cbd5e1", fontSize: 12, marginBottom: 8,
        }}>
          Drop leads here
        </div>
      )}

      {/* Lead cards */}
      <div style={{ minHeight: 40 }}>
        {leads.map(lead => (
          <LeadCard
            key={lead.id}
            lead={lead}
            stageCfg={stage}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onReopen={onReopen}
            onOpenLead={onOpenLead}
            snoozed={lead.snoozed_until && new Date(lead.snoozed_until) > new Date()}
            isBeingDragged={draggedLeadId === lead.id}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const PipelineBoard = () => {
  const companyId = DataParser.CompanyId();

  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");

  // Drag state (card DnD)
  const dragLeadRef = useRef(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [draggedLeadId, setDraggedLeadId] = useState(null);

  // Board pan-scroll (JIRA-style mouse drag)
  const boardRef  = useRef(null);
  const boardDrag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const onBoardMouseDown = (e) => {
    if (e.target.closest('[draggable="true"]')) return;
    const board = boardRef.current;
    boardDrag.current = { active: true, startX: e.pageX - board.offsetLeft, scrollLeft: board.scrollLeft };
    board.style.cursor = "grabbing";
  };

  const onBoardMouseMove = (e) => {
    if (!boardDrag.current.active) return;
    e.preventDefault();
    const board = boardRef.current;
    board.scrollLeft = boardDrag.current.scrollLeft - (e.pageX - board.offsetLeft - boardDrag.current.startX);
  };

  const onBoardMouseUp = () => {
    if (!boardDrag.current.active) return;
    boardDrag.current.active = false;
    boardRef.current.style.cursor = "grab";
  };

  // Lead detail modal
  const [openLeadId, setOpenLeadId] = useState(null);

  // Lost transition modal
  const [lostModal, setLostModal]   = useState(false);
  const [lostReason, setLostReason] = useState(null);
  const [lostNotes, setLostNotes]   = useState("");
  const [lostTarget, setLostTarget] = useState(null);

  // Won transition modal
  const [wonModal, setWonModal]     = useState(false);
  const [wonAmount, setWonAmount]   = useState(null);
  const [wonRef, setWonRef]         = useState("");
  const [wonTarget, setWonTarget]   = useState(null);

  // Reopen modal
  const [reopenModal, setReopenModal]     = useState(false);
  const [reopenTarget, setReopenTarget]   = useState(null);
  const [reopenLoading, setReopenLoading] = useState(false);

  const [transitioning, setTransitioning] = useState(false);

  const fetchLeads = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await CrmLeadsServices.getLeads(companyId);
      if (Array.isArray(res)) {
        setLeads(res);
      } else if (res?.leads && Array.isArray(res.leads)) {
        setLeads(res.leads);
      } else if (res?.data && Array.isArray(res.data)) {
        setLeads(res.data);
      } else {
        setLeads([]);
      }
    } catch {
      message.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    SocketService.connect();
    const handler = (data) => {
      if (String(data?.company_id) !== String(companyId)) return;

      if (data?.action === "activity_logged") {
        const leadId = data?.data?.lead_id;
        if (leadId) {
          setLeads(prev => prev.map(l =>
            l.id === leadId ? { ...l, last_activity_at: new Date().toISOString() } : l
          ));
        }
        return;
      }

      if (data?.action === "create") {
        fetchLeads(true);
        return;
      }

      if ((data?.action === "update" || data?.action === "stage_changed") && data?.data?.id) {
        setLeads(prev => prev.map(l =>
          l.id === data.data.id ? { ...l, ...data.data } : l
        ));
        return;
      }

      if (data?.action === "delete" && data?.data?.id) {
        setLeads(prev => prev.filter(l => l.id !== data.data.id));
        return;
      }

      fetchLeads(true);
    };
    SocketService.on("lead_update", handler);
    return () => SocketService.off("lead_update", handler);
  }, [fetchLeads, companyId]);

  // ── Drag handlers ──────────────────────────────────────────────────────────
  const handleDragStart = (e, lead) => {
    dragLeadRef.current = lead;
    e.dataTransfer.effectAllowed = "move";
    // Show dashed placeholder AFTER browser captures the drag ghost
    setTimeout(() => setDraggedLeadId(lead.id), 0);
  };

  const handleDragEnd = () => {
    setDraggedLeadId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    setDragOverStage(stageKey);
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    setDraggedLeadId(null);
    const lead = dragLeadRef.current;
    if (!lead) return;
    if (lead.stage === targetStage) return;
    if (TERMINAL_STAGES.includes(lead.stage)) {
      message.info(`Use the Reopen button to move a ${lead.stage} lead.`);
      return;
    }

    // Pre-validate before opening any modal — mirrors backend TRANSITION_RULES
    const transitionError = getTransitionError(lead.stage, targetStage);
    if (transitionError) {
      message.error(transitionError);
      return;
    }

    if (targetStage === "won") {
      setWonTarget({ lead, targetStage });
      setWonAmount(null);
      setWonRef("");
      setWonModal(true);
      return;
    }
    if (targetStage === "lost") {
      setLostTarget({ lead, targetStage });
      setLostReason(null);
      setLostNotes("");
      setLostModal(true);
      return;
    }
    doTransition(lead, targetStage, {});
  };

  // ── Transition helpers ─────────────────────────────────────────────────────
  const doTransition = async (lead, targetStage, extra = {}) => {
    setTransitioning(true);
    try {
      await LeadPipelineService.transitionStage(lead.id, {
        company_id: companyId,
        stage:      targetStage,
        ...extra,
      });
      message.success(`Lead moved to ${STAGES.find(s => s.key === targetStage)?.label || targetStage}.`);
      fetchLeads();
    } catch (err) {
      const msg = err?.response?.data?.data || err?.response?.data?.message || err?.message || "Stage transition failed.";
      message.error(msg);
    } finally {
      setTransitioning(false);
    }
  };

  const handleLostConfirm = async () => {
    if (!lostReason) { message.warning("Please select a lost reason."); return; }
    setLostModal(false);
    await doTransition(lostTarget.lead, "lost", {
      lost_reason: lostReason,
      lost_notes:  lostNotes,
    });
    setLostTarget(null);
  };

  const handleWonConfirm = async () => {
    setWonModal(false);
    await doTransition(wonTarget.lead, "won", {
      won_amount:    wonAmount || 0,
      won_reference: wonRef,
    });
    setWonTarget(null);
  };

  const handleReopen = (lead) => {
    setReopenTarget(lead);
    setReopenModal(true);
  };

  const handleReopenConfirm = async () => {
    setReopenLoading(true);
    try {
      await LeadPipelineService.reopenLead(reopenTarget.id, {
        company_id:   companyId,
        target_stage: "new",
      });
      message.success("Lead re-opened and moved to New.");
      setReopenModal(false);
      fetchLeads();
    } catch (err) {
      message.error(err?.response?.data?.data || err?.response?.data?.message || "Failed to re-open lead.");
    } finally {
      setReopenLoading(false);
    }
  };

  // ── Group leads by stage — memoized so search/stage changes don't recompute on every render ──
  const visibleLeads = useMemo(() => {
    if (!search) return leads;
    const filterLower = search.toLowerCase();
    return leads.filter(l =>
      (l.lead_title || l.title || "").toLowerCase().includes(filterLower) ||
      (l.lead_contact_name || "").toLowerCase().includes(filterLower) ||
      (l.lead_phone || "").includes(filterLower)
    );
  }, [leads, search]);

  const byStage = useMemo(() => {
    const result = {};
    STAGES.forEach(s => { result[s.key] = []; });
    visibleLeads.forEach(lead => {
      const stage = lead.stage || "new";
      if (result[stage]) result[stage].push(lead);
    });
    return result;
  }, [visibleLeads]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const uncontactedCount = leads.filter(l => l.workflow_type === "inbound" && l.stage === "new").length;

  return (
    <div className="main-content">
      <style>{`
        .pb-board::-webkit-scrollbar { display: none; }
        @media (min-width: 993px) {
          .pb-column {
            width: calc((100% - 30px) / 6);
            flex: 1 1 auto;
            min-width: 145px;
          }
        }
        @media (max-width: 992px) {
          .pb-column {
            min-width: 228px;
            max-width: 248px;
            flex: 0 0 228px;
          }
        }
        .task-search-wrap { position: relative; width: 250px; flex-shrink: 0; }
        .task-search-wrap input { width: 100%; height: 34px; padding: 0 36px 0 14px; border: 1.5px solid #F2F2F2; border-radius: 5px; font-size: 13px; outline: none; background: #fff; }
        .task-search-wrap input:focus { border-color: #1D1D1D !important; box-shadow: none !important; }
        .task-search-wrap:hover input { border-color: #1D1D1D !important; }
        .task-search-wrap label { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); font-size: 13px; color: #D2D2D2; pointer-events: none; transition: all 0.2s ease; background: #fff; padding: 0 2px; }
        .task-search-wrap input:focus ~ label, .task-search-wrap input:not(:placeholder-shown) ~ label { top: 0; font-size: 11px; font-weight: 600; color: #1D1D1D; }
        .task-search-wrap:hover label, .task-search-wrap:focus-within label { color: #1D1D1D !important; }
        .task-search-wrap i { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: #D2D2D2; font-size: 16px; transition: all 0.2s ease; }
        .task-search-wrap input:focus ~ i { color: #1D1D1D !important; }
        .task-search-wrap:hover i, .task-search-wrap:focus-within i { color: #1D1D1D !important; }
      `}</style>
      <div className="page-content" style={{ minHeight: "100vh", backgroundColor: "#ffffff" }}>
        <div className="container-fluid" style={{ padding: "24px", paddingBottom: "60px" }}>
      {/* Header */}
      <div className="pb-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <p style={{ margin: 0, color: "#0DDFAB", fontSize: 13 }}>
          {visibleLeads.length} lead{visibleLeads.length !== 1 ? "s" : ""} · drag cards between stages to transition
        </p>
        <div className="task-search-wrap">
          <input
            type="text"
            id="pipelineSearchInput"
            placeholder=" "
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <label htmlFor="pipelineSearchInput">Search Leads...</label>
          {search
            ? <i className="uil uil-times" onMouseDown={(e) => { e.preventDefault(); setSearch(''); }} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', fontSize: '15px', cursor: 'pointer', zIndex: 20 }} />
            : <i className="uil uil-search" />
          }
        </div>
      </div>

      {loading ? (
        <div className="table-loader-overlay" style={{ flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', padding: '12px 16px', background: '#fff', gap: '8px', overflow: 'hidden' }}>
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
      ) : (
        <>
          {/* Board */}
          <div
            ref={boardRef}
            className="pb-board"
            onMouseDown={onBoardMouseDown}
            onMouseMove={onBoardMouseMove}
            onMouseUp={onBoardMouseUp}
            onMouseLeave={onBoardMouseUp}
            style={{
              display: "flex", gap: 6, overflowX: "auto",
              paddingBottom: 16, alignItems: "flex-start",
              cursor: "grab", userSelect: "none",
              scrollbarWidth: "none", msOverflowStyle: "none",
            }}
          >
            {STAGES.map(stage => (
              <PipelineColumn
                key={stage.key}
                stage={stage}
                leads={byStage[stage.key] || []}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onReopen={handleReopen}
                onOpenLead={setOpenLeadId}
                draggedLeadId={draggedLeadId}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>

          {/* SLA legend bar */}
          <div className="pb-sla-bar" style={{
            background: "#1e293b", color: "#e2e8f0",
            padding: "12px 24px", display: "flex", alignItems: "center",
            gap: 20, fontSize: 12, flexWrap: "wrap",
            borderRadius: "8px", marginTop: "16px"
          }}>
            <span style={{ fontWeight: 600, color: "#fff" }}>SLA Indicators:</span>
            {[
              { color: "#10b981", label: "On Track (>2h)" },
              { color: "#f59e0b", label: "Approaching (2-22h)" },
              { color: "#ef4444", label: "Breached (<24h)" },
            ].map(({ color, label }) => (
              <span key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
                {label}
              </span>
            ))}
            {uncontactedCount > 0 && (
              <span style={{ marginLeft: "auto", color: "#94a3b8" }}>
                Uncontacted [{uncontactedCount}] inbound leads in "New" stage have SLA
              </span>
            )}
          </div>
        </>
      )}

      {/* Lost modal */}
      <Modal
        title={
          <span style={{ color: "#ef4444", fontWeight: 600 }}>
            <FiAlertTriangle style={{ marginRight: 8 }} />
            Mark as Lost
          </span>
        }
        open={lostModal}
        onOk={handleLostConfirm}
        onCancel={() => { setLostModal(false); setLostTarget(null); }}
        okText="Mark Lost"
        okButtonProps={{ danger: true }}
        confirmLoading={transitioning}
      >
        <p style={{ color: "#64748b", marginBottom: 16 }}>
          Moving <strong>{lostTarget?.lead?.lead_title || `Lead #${lostTarget?.lead?.id}`}</strong> to Lost.
          Please provide a reason.
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
            Lost Reason <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <Select
            placeholder="Select reason"
            style={{ width: "100%" }}
            value={lostReason}
            onChange={setLostReason}
            getPopupContainer={trigger => trigger.parentElement}
          >
            {LOST_REASONS.map(r => (
              <Option key={r.value} value={r.value}>{r.label}</Option>
            ))}
          </Select>
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
            Notes (optional)
          </label>
          <TextArea
            rows={3}
            placeholder="Additional context…"
            value={lostNotes}
            onChange={e => setLostNotes(e.target.value)}
          />
        </div>
      </Modal>

      {/* Won modal */}
      <Modal
        title={
          <span style={{ color: "#10b981", fontWeight: 600 }}>
            🎉 Mark as Won
          </span>
        }
        open={wonModal}
        onOk={handleWonConfirm}
        onCancel={() => { setWonModal(false); setWonTarget(null); }}
        okText="Mark Won"
        okButtonProps={{ style: { background: "#10b981", borderColor: "#10b981" } }}
        confirmLoading={transitioning}
      >
        <p style={{ color: "#64748b", marginBottom: 16 }}>
          Congratulations! Record the winning details for{" "}
          <strong>{wonTarget?.lead?.lead_title || `Lead #${wonTarget?.lead?.id}`}</strong>.
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
            Won Amount (PKR)
          </label>
          <InputNumber
            style={{ width: "100%" }}
            placeholder="0"
            min={0}
            value={wonAmount}
            onChange={setWonAmount}
            formatter={v => v ? String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
            parser={v => v.replace(/,/g, "")}
          />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
            Reference / PO Number (optional)
          </label>
          <Input
            placeholder="e.g. PO-2024-001"
            value={wonRef}
            onChange={e => setWonRef(e.target.value)}
          />
        </div>
      </Modal>

      {/* Reopen modal */}
      <Modal
        title={
          <span style={{ color: "#3b82f6", fontWeight: 600 }}>
            <FiRotateCcw style={{ marginRight: 8 }} />
            Re-open Lead
          </span>
        }
        open={reopenModal}
        onOk={handleReopenConfirm}
        onCancel={() => setReopenModal(false)}
        okText="Re-open"
        okButtonProps={{ style: { background: "#3b82f6", borderColor: "#3b82f6" } }}
        confirmLoading={reopenLoading}
      >
        <p style={{ color: "#64748b" }}>
          Re-open <strong>{reopenTarget?.lead_title || reopenTarget?.title || `Lead #${reopenTarget?.id}`}</strong>?
          It will be moved back to <strong>New</strong> stage.
        </p>
      </Modal>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin-icon { animation: spin 1s linear infinite; }
        @media (max-width: 575px) {
          .pb-header { flex-direction: column; align-items: flex-start !important; }
          .pb-header-actions { width: 100%; }
          .pb-search { width: 100% !important; }
        }
      `}</style>
        </div>
      </div>

      {/* Lead detail modal — opens inline without leaving Pipeline Board */}
      {openLeadId && (
        <div
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100005 }}
          onClick={() => setOpenLeadId(null)}
        >
          <CreateCrmLead
            editId={openLeadId}
            canEditLead={true}
            onCancel={() => setOpenLeadId(null)}
            onSuccess={() => setOpenLeadId(null)}
          />
        </div>
      )}
    </div>
  );
};

export default PipelineBoard;
